import { DataProvider, type DataClient } from '@numistic/data'
import { createI18n, I18nextProvider } from '@numistic/i18n'
import type { QueryClient } from '@tanstack/react-query'
import { createRouter, RouterProvider, type RouterHistory } from '@tanstack/react-router'
import { useState } from 'react'

import { ErrorView } from './components/error-view.js'
import { setupValidationMessages } from './lib/errors.js'
import { adminInvitesRoute } from './routes/admin/invites.js'
import { adminRoute } from './routes/admin/layout.js'
import { adminReferencesRoute } from './routes/admin/references.js'
import { adminTypesRoute } from './routes/admin/types.js'
import { adminUsersRoute } from './routes/admin/users.js'
import { forgotPasswordRoute } from './routes/auth/forgot-password.js'
import { inviteRoute } from './routes/auth/invite.js'
import { loginRoute } from './routes/auth/login.js'
import { resetPasswordRoute } from './routes/auth/reset-password.js'
import { catalogEditRoute } from './routes/catalog/edit.js'
import { catalogItemRoute } from './routes/catalog/item.js'
import { catalogRoute } from './routes/catalog/list.js'
import { shareRoute, shareSpecimenRoute } from './routes/share.js'
import { adminMetaRoute } from './admin-meta/page.js'
import { adminChangesRoute } from './routes/admin/changes.js'
import { statsRoute } from './routes/stats.js'
import { profileRoute } from './routes/settings/profile.js'
import { dataRoute } from './routes/settings/data.js'
import { numistaImportRoute } from './routes/catalog/import-numista.js'
import { catalogNewRoute } from './routes/catalog/new.js'
import { collectionsRoute } from './routes/collections.js'
import { homeRoute } from './routes/home.js'
import { specimenEditRoute } from './routes/specimens/edit.js'
import { specimenRoute } from './routes/specimens/item.js'
import { specimenNewRoute } from './routes/specimens/new.js'
import { forSaleRoute, savedViewRoute, wantedRoute } from './routes/specimens/views.js'
import { rootRoute } from './routes/root.js'
import { devicesRoute } from './routes/settings/devices.js'
import { locationsRoute } from './routes/settings/locations.js'
import { settingsCollectionsRoute } from './routes/settings/collections.js'
import { settingsIndexRoute, settingsRoute } from './routes/settings/layout.js'
import { settingsTagsRoute } from './routes/settings/tags.js'
import { settingsCounterpartiesRoute } from './routes/settings/counterparties.js'

export const routeTree = rootRoute.addChildren([
  homeRoute,
  catalogRoute,
  catalogNewRoute,
  numistaImportRoute,
  statsRoute,
  shareRoute,
  shareSpecimenRoute,
  catalogItemRoute,
  catalogEditRoute,
  collectionsRoute,
  specimenNewRoute,
  specimenRoute,
  specimenEditRoute,
  wantedRoute,
  forSaleRoute,
  savedViewRoute,
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  inviteRoute,
  settingsRoute.addChildren([
    settingsIndexRoute,
    profileRoute,
    settingsCollectionsRoute,
    locationsRoute,
    settingsTagsRoute,
    settingsCounterpartiesRoute,
    devicesRoute,
    dataRoute,
  ]),
  adminRoute.addChildren([
    adminUsersRoute,
    adminInvitesRoute,
    adminReferencesRoute,
    adminTypesRoute,
    adminMetaRoute,
    adminChangesRoute,
  ]),
])

export interface AppDeps {
  dataClient: DataClient
  queryClient: QueryClient
  /** Для тестов: память вместо адресной строки браузера. */
  history?: RouterHistory
}

export function createAppRouter({ dataClient, queryClient, history }: AppDeps) {
  return createRouter({
    routeTree,
    history,
    context: { dataClient, queryClient },
    defaultPreload: 'intent',
    defaultErrorComponent: ErrorView,
    // Свежесть данных контролирует TanStack Query, роутер не кэширует loader'ы сам.
    defaultPreloadStaleTime: 0,
  })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>
  }
}

setupValidationMessages()

/** Корневой компонент, общий для web и будущего desktop: различается только DataClient. */
export function App({ router }: { router: ReturnType<typeof createAppRouter> }) {
  const [i18n] = useState(() => createI18n('ru'))
  const { dataClient, queryClient } = router.options.context
  return (
    <I18nextProvider i18n={i18n}>
      <DataProvider client={dataClient} queryClient={queryClient}>
        <RouterProvider router={router} />
      </DataProvider>
    </I18nextProvider>
  )
}
